// RoleGuard - Protects routes based on user role
// Only users with 'approved' or 'admin' roles can access protected pages

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';

interface RoleGuardProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

// Default allowed roles for protected pages
const DEFAULT_ALLOWED_ROLES = ['approved', 'admin'];

export const RoleGuard: React.FC<RoleGuardProps> = ({ 
  children, 
  allowedRoles = DEFAULT_ALLOWED_ROLES 
}) => {
  const { user, isAuthenticated } = useAuthStore();
  const location = useLocation();

  // If not authenticated, redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  // Check if user's role is allowed
  const userRole = user.role || 'alpha';
  if (!allowedRoles.includes(userRole)) {
    // User doesn't have required role - show access denied
    return (
      <div className="min-h-screen bg-glass-tertiary flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-glass-secondary rounded-lg shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg 
              className="w-8 h-8 text-yellow-500" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 15v2m0 0v2m0-2h2m-2 0H10m2-10V4a2 2 0 00-2-2H8a2 2 0 00-2 2v1m8 0V4a2 2 0 012-2h2a2 2 0 012 2v1M5 9h14a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2v-9a2 2 0 012-2z" 
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Access Pending</h2>
          <p className="text-gray-400 mb-4">
            Your account is pending approval for access to this feature.
          </p>
          <p className="text-gray-500 text-sm mb-6">
            You're currently signed in as <span className="text-gray-300">{user.email}</span> with role <span className="text-yellow-500">{userRole}</span>.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => window.location.href = '/dashboard'}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Go to Intelligence Home
            </button>
            <p className="text-gray-500 text-xs">
              Contact an administrator to request access to topology and tuning features.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // User has required role - render children
  return <>{children}</>;
};

export default RoleGuard;
